import React, { useEffect, useState } from "react";
import { Download, Users, Building2, ListChecks } from "lucide-react";
import { api } from "@/lib/api";

/**
 * PULA LEARN — admin waitlist viewer + CSV export.
 * Requires an admin session (same auth as the host app's admin dashboard).
 */

export default function AdminLearnWaitlistPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/learn/admin/waitlist")
      .then(({ data }) => setData(data))
      .catch((err) => setError(err?.response?.data?.detail || "Failed to load"));
  }, []);

  const downloadCsv = async () => {
    try {
      const res = await api.get("/learn/admin/waitlist.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "pula-learn-waitlist.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("CSV download failed");
    }
  };

  return (
    <main className="bg-white min-h-[60vh] py-12" data-testid="admin-learn-waitlist-page">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Pula Learn — Launch Waiting List</h1>
            <p className="text-sm text-gray-500">Pre-launch signups. Email this list the day CPD accreditation lands.</p>
          </div>
          <button onClick={downloadCsv} data-testid="learn-waitlist-csv-btn"
            className="btn-green inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors">
            <Download size={16} /> Export CSV
          </button>
        </div>

        {error && <p className="text-sm text-red-600 mb-6" data-testid="learn-waitlist-error">{error}</p>}

        {data && (
          <>
            <div className="grid grid-cols-3 gap-4 mb-8">
              {[
                { icon: ListChecks, label: "Total signups", value: data.total, tid: "stat-total" },
                { icon: Users, label: "Individuals", value: data.individuals, tid: "stat-individuals" },
                { icon: Building2, label: "Employers", value: data.employers, tid: "stat-employers" },
              ].map(({ icon: Icon, label, value, tid }) => (
                <div key={tid} className="rounded-xl border mv-hairline p-5 text-center" data-testid={tid}>
                  <Icon size={20} className="mx-auto mb-2 mv-green-text" />
                  <p className="text-2xl font-bold">{value}</p>
                  <p className="text-xs text-gray-500">{label}</p>
                </div>
              ))}
            </div>

            <div className="rounded-xl border mv-hairline overflow-x-auto">
              <table className="w-full text-sm" data-testid="learn-waitlist-table">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">Email</th>
                    <th className="px-4 py-3 font-semibold">Interest</th>
                    <th className="px-4 py-3 font-semibold">Bundle</th>
                    <th className="px-4 py-3 font-semibold">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No signups yet.</td></tr>
                  )}
                  {data.rows.map((r) => (
                    <tr key={r.id} className="border-t mv-hairline" data-testid={`waitlist-row-${r.email}`}>
                      <td className="px-4 py-3">{r.name}</td>
                      <td className="px-4 py-3">{r.email}</td>
                      <td className="px-4 py-3 capitalize">{r.interest}</td>
                      <td className="px-4 py-3 text-gray-500">{r.bundle_slug || "—"}</td>
                      <td className="px-4 py-3 text-gray-500">{r.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
